import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { SunsetAPIFactionContext } from "../../../Networking/Interface/Faction/FactionApiProvider";
import { Faction } from "../../../Networking/Interface/Faction/FactionInterface";
import {
  War,
  WarAction,
  WarParticipant,
  WarSummary,
} from "../../../Networking/Interface/Faction/WarInterface";
import { useToast } from "../../../Components/Toast/ToastContext";
import { ToastType } from "../../../Components/Toast/Toast";
import styles from "./WarsPage.module.css";

// ---- optional column definitions ----
interface ToggleColumn {
  key: string;
  label: string;
  defaultOn: boolean;
}

const OPTIONAL_COLUMNS: ToggleColumn[] = [
  { key: "suicides", label: "Suicides", defaultOn: true },
  { key: "damage", label: "Damage", defaultOn: true },
  { key: "playtime", label: "Playtime", defaultOn: true },
  { key: "sniperKills", label: "Sniper Hunter Kills", defaultOn: true },
];

// ---- helpers ----
function formatDuration(start: string, end: string): string {
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (ms <= 0) return "0 minutes";
  const totalMinutes = Math.floor(ms / 60000);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  const parts: string[] = [];
  if (days > 0) parts.push(`${days} day${days !== 1 ? "s" : ""}`);
  if (hours > 0) parts.push(`${hours} hour${hours !== 1 ? "s" : ""}`);
  if (minutes > 0) parts.push(`${minutes} minute${minutes !== 1 ? "s" : ""}`);
  return parts.join(", ") || "0 minutes";
}

// Per-player aggregated row
interface PlayerRow {
  userId: number;
  factionId: number;
  name: string; // will try using the userId for now
  kills: number;
  deaths: number;
  suicides: number;
  damage: number;
  playtime: number; // placeholder – API doesn't expose this
  sniperKills: number;
}

type SortKey = "kills" | "deaths" | "kdr" | "suicides" | "damage" | "playtime" | "sniperKills";

function buildPlayerRows(actions: WarAction[]): PlayerRow[] {
  const map = new Map<number, PlayerRow>();

  const ensure = (uid: number, fid: number, name?: string | null): PlayerRow => {
    if (!map.has(uid)) {
      map.set(uid, {
        userId: uid,
        factionId: fid,
        name: name || `Player ${uid}`,
        kills: 0,
        deaths: 0,
        suicides: 0,
        damage: 0,
        playtime: 0,
        sniperKills: 0,
      });
    }
    const row = map.get(uid)!;
    // Update name if we got a real one and currently have a fallback
    if (name && row.name === `Player ${uid}`) {
      row.name = name;
    }
    return row;
  };

  for (const a of actions) {
    // issuer gets a kill
    const issuer = ensure(a.issuer_id, a.faction_issuer_id, a.issuer_name);
    issuer.kills += 1;
    issuer.damage += a.money_change;

    if (a.user_id_sniper > 0) {
      // if there's a sniper user, credit sniper kills to the sniper
      const sniper = ensure(a.user_id_sniper, a.faction_issuer_id, a.sniper_name);
      sniper.sniperKills += 1;
    }

    // target gets a death
    if (a.user_id !== a.issuer_id) {
      const target = ensure(a.user_id, a.faction_user_id, a.user_name);
      target.deaths += 1;
    } else {
      // self-kill counts as suicide
      issuer.suicides += 1;
      issuer.kills -= 1; // don't count self-kill as a real kill
      issuer.deaths += 1;
    }
  }

  return Array.from(map.values());
}

function getKdr(kills: number, deaths: number): string {
  if (deaths === 0) return kills > 0 ? kills.toFixed(2) : "0.00";
  return (kills / deaths).toFixed(2);
}

// ---- Component ----
const WarsPage: React.FC = () => {
  const api = useContext(SunsetAPIFactionContext);
  const { showToast } = useToast();

  // data
  const [wars, setWars] = useState<War[]>([]);
  const [factions, setFactions] = useState<Faction[]>([]);
  const [selectedWarId, setSelectedWarId] = useState<number | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [summary, setSummary] = useState<WarSummary | null>(null);
  const [actions, setActions] = useState<WarAction[]>([]);
  const [participants, setParticipants] = useState<WarParticipant[]>([]);

  // UI
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // column toggles
  const [visibleCols, setVisibleCols] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {};
    OPTIONAL_COLUMNS.forEach((c) => (init[c.key] = c.defaultOn));
    return init;
  });

  // sort
  const [sortKey, setSortKey] = useState<SortKey>("kills");
  const [sortAsc, setSortAsc] = useState(false);

  // pagination
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  // faction lookup
  const factionMap = useMemo(() => {
    const m = new Map<number, Faction>();
    factions.forEach((f) => m.set(f.id, f));
    return m;
  }, [factions]);

  const factionName = useCallback(
    (id: number) => factionMap.get(id)?.name ?? `Faction ${id}`,
    [factionMap],
  );

  // ---- fetch wars + factions ----
  useEffect(() => {
    if (!api) return;
    (async () => {
      setLoading(true);
      const [warsRes, factionsRes] = await Promise.all([
        api.getWars(),
        api.getFactions(),
      ]);
      if (warsRes.code === 429 || factionsRes.code === 429) {
        showToast(ToastType.Warning, "Slow down! You're being rate limited.", 4);
        setLoading(false);
        return;
      }
      if (warsRes.success && warsRes.data) {
        setWars(warsRes.data);
        if (warsRes.data.length > 0) setSelectedWarId(warsRes.data[0].id);
      } else {
        setError(warsRes.error || "Failed to load wars.");
      }
      if (factionsRes.success && factionsRes.data) {
        setFactions(factionsRes.data);
      }
      setLoading(false);
    })();
  }, [api, showToast]);

  // ---- fetch war detail when selection changes ----
  useEffect(() => {
    if (!api || selectedWarId == null) return;
    (async () => {
      setDetailLoading(true);
      setPage(0);
      const [summaryRes, actionsRes, participantsRes] = await Promise.all([
        api.getWarSummary(selectedWarId),
        api.getWarActions(selectedWarId),
        api.getWarParticipants(selectedWarId),
      ]);
      if (summaryRes.code === 429 || actionsRes.code === 429 || participantsRes.code === 429) {
        showToast(ToastType.Warning, "Go easy! You're being rate limited.", 4);
        setDetailLoading(false);
        return;
      }
      if (summaryRes.success && summaryRes.data) setSummary(summaryRes.data);
      else setSummary(null);

      if (actionsRes.success && actionsRes.data) setActions(actionsRes.data);
      else setActions([]);

      if (participantsRes.success && participantsRes.data) setParticipants(participantsRes.data);
      else setParticipants([]);

      setDetailLoading(false);
    })();
  }, [api, selectedWarId, showToast]);

  // ---- derived ----
  const selectedWar = useMemo(
    () => wars.find((w) => w.id === selectedWarId) ?? null,
    [wars, selectedWarId],
  );

  const team1Participants = useMemo(() => participants.filter((p) => p.team === 1), [participants]);
  const team2Participants = useMemo(() => participants.filter((p) => p.team === 2), [participants]);

  const team1FactionIds = useMemo(() => new Set(team1Participants.map((p) => p.faction_id)), [team1Participants]);
  const team2FactionIds = useMemo(() => new Set(team2Participants.map((p) => p.faction_id)), [team2Participants]);

  // aggregate team stats from player rows
  const playerRows = useMemo(() => buildPlayerRows(actions), [actions]);

  const teamStats = useMemo(() => {
    const calc = (factionIds: Set<number>) => {
      const rows = playerRows.filter((r) => factionIds.has(r.factionId));
      const kills = rows.reduce((s, r) => s + r.kills, 0);
      const deaths = rows.reduce((s, r) => s + r.deaths, 0);
      const suicides = rows.reduce((s, r) => s + r.suicides, 0);
      const damage = rows.reduce((s, r) => s + r.damage, 0);
      return { kills, deaths, kdr: getKdr(kills, deaths), suicides, damage };
    };
    return { team1: calc(team1FactionIds), team2: calc(team2FactionIds) };
  }, [playerRows, team1FactionIds, team2FactionIds]);

  // team display names
  const team1Name = useMemo(
    () => team1Participants.map((p) => factionName(p.faction_id)).join(" & ") || "Team 1",
    [team1Participants, factionName],
  );
  const team2Name = useMemo(
    () => team2Participants.map((p) => factionName(p.faction_id)).join(" & ") || "Team 2",
    [team2Participants, factionName],
  );

  // sorted + paginated player rows
  const sortedRows = useMemo(() => {
    const rows = [...playerRows];
    rows.sort((a, b) => {
      let av: number, bv: number;
      switch (sortKey) {
        case "kills": av = a.kills; bv = b.kills; break;
        case "deaths": av = a.deaths; bv = b.deaths; break;
        case "kdr": av = parseFloat(getKdr(a.kills, a.deaths)); bv = parseFloat(getKdr(b.kills, b.deaths)); break;
        case "suicides": av = a.suicides; bv = b.suicides; break;
        case "damage": av = a.damage; bv = b.damage; break;
        case "playtime": av = a.playtime; bv = b.playtime; break;
        case "sniperKills": av = a.sniperKills; bv = b.sniperKills; break;
        default: av = a.kills; bv = b.kills;
      }
      return sortAsc ? av - bv : bv - av;
    });
    return rows;
  }, [playerRows, sortKey, sortAsc]);

  const paginatedRows = useMemo(() => {
    const start = page * pageSize;
    return sortedRows.slice(start, start + pageSize);
  }, [sortedRows, page, pageSize]);

  const totalPages = Math.ceil(sortedRows.length / pageSize);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortAsc((p) => !p);
    else { setSortKey(key); setSortAsc(false); }
  };

  const sortIndicator = (key: SortKey) =>
    sortKey === key ? <span className={styles.sortArrow}>{sortAsc ? "↑" : "↓"}</span> : null;

  // ---- render ----
  if (loading) return <div className={`card ${styles.wrapper}`}><div className={styles.loading}>Loading wars...</div></div>;
  if (error) return <div className={`card ${styles.wrapper}`}><div className={styles.error}>{error}</div></div>;
  if (wars.length === 0) return <div className={`card ${styles.wrapper}`}><div className={styles.emptyState}>No wars found.</div></div>;

  const isTeam1Winner = selectedWar ? selectedWar.winner === 1 : false;
  const isTeam2Winner = selectedWar ? selectedWar.winner === 2 : false;

  return (
    <div className={`card ${styles.wrapper}`}>
      <h2 className={styles.pageTitle}>Faction Wars</h2>
      <p className={styles.pageSubtitle}>View statistics and kill lists for faction wars.</p>

      {/* War selector */}
      <div className={styles.controls}>
        <span className={styles.selectLabel}>Select War:</span>
        <select
          className={styles.warSelect}
          value={selectedWarId ?? ""}
          onChange={(e) => setSelectedWarId(Number(e.target.value))}
        >
          {wars.map((w) => (
            <option key={w.id} value={w.id}>
              War #{w.id}
            </option>
          ))}
        </select>

        {selectedWar && (
          <span className={styles.warDuration}>
            {formatDuration(selectedWar.start_date, selectedWar.end_date)}
          </span>
        )}
      </div>

      {detailLoading ? (
        <div className={styles.loading}>Loading war details...</div>
      ) : (
        <>
          {/* Team cards */}
          <div className={styles.teamsRow}>
            {/* Team 1 */}
            <div className={styles.teamCard}>
              {isTeam1Winner && <span className={styles.winnerBadge}>(Winner)</span>}
              <div className={`${styles.teamName} ${styles.team1Name}`}>{team1Name}</div>
              <div className={styles.teamStats}>
                <div className={styles.teamStat}>
                  <div className={styles.teamStatLabel}>Kills</div>
                  <div className={styles.teamStatValue}>{teamStats.team1.kills.toLocaleString()}</div>
                </div>
                <div className={styles.teamStat}>
                  <div className={styles.teamStatLabel}>Deaths</div>
                  <div className={styles.teamStatValue}>{teamStats.team1.deaths.toLocaleString()}</div>
                </div>
                <div className={styles.teamStat}>
                  <div className={styles.teamStatLabel}>KDR</div>
                  <div className={styles.teamStatValue}>{teamStats.team1.kdr}</div>
                </div>
                <div className={styles.teamStat}>
                  <div className={styles.teamStatLabel}>Suicides</div>
                  <div className={styles.teamStatValue}>{teamStats.team1.suicides.toLocaleString()}</div>
                </div>
                <div className={styles.teamStat}>
                  <div className={styles.teamStatLabel}>Damage</div>
                  <div className={styles.teamStatValue}>{teamStats.team1.damage.toLocaleString()}</div>
                </div>
              </div>
            </div>

            {/* Team 2 */}
            <div className={styles.teamCard}>
              {isTeam2Winner && <span className={styles.winnerBadge}>(Winner)</span>}
              <div className={`${styles.teamName} ${styles.team2Name}`}>{team2Name}</div>
              <div className={styles.teamStats}>
                <div className={styles.teamStat}>
                  <div className={styles.teamStatLabel}>Kills</div>
                  <div className={styles.teamStatValue}>{teamStats.team2.kills.toLocaleString()}</div>
                </div>
                <div className={styles.teamStat}>
                  <div className={styles.teamStatLabel}>Deaths</div>
                  <div className={styles.teamStatValue}>{teamStats.team2.deaths.toLocaleString()}</div>
                </div>
                <div className={styles.teamStat}>
                  <div className={styles.teamStatLabel}>KDR</div>
                  <div className={styles.teamStatValue}>{teamStats.team2.kdr}</div>
                </div>
                <div className={styles.teamStat}>
                  <div className={styles.teamStatLabel}>Suicides</div>
                  <div className={styles.teamStatValue}>{teamStats.team2.suicides.toLocaleString()}</div>
                </div>
                <div className={styles.teamStat}>
                  <div className={styles.teamStatLabel}>Damage</div>
                  <div className={styles.teamStatValue}>{teamStats.team2.damage.toLocaleString()}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Column toggles */}
          <div className={styles.columnToggles}>
            {OPTIONAL_COLUMNS.map((col) => (
              <label key={col.key} className={styles.toggleLabel}>
                <input
                  type="checkbox"
                  checked={visibleCols[col.key]}
                  onChange={() =>
                    setVisibleCols((prev) => ({ ...prev, [col.key]: !prev[col.key] }))
                  }
                />
                {col.label}
              </label>
            ))}
          </div>

          {/* Kill List */}
          <div className={styles.killListHeader}>Kill List</div>

          {sortedRows.length === 0 ? (
            <div className={styles.emptyState}>No actions recorded for this war.</div>
          ) : (
            <>
              <div className={styles.tableWrapper}>
                <table className={styles.killTable}>
                  <thead>
                    <tr>
                      <th style={{ width: 40 }}>#</th>
                      <th>Name</th>
                      <th onClick={() => handleSort("kills")}>Kills {sortIndicator("kills")}</th>
                      <th onClick={() => handleSort("deaths")}>Deaths {sortIndicator("deaths")}</th>
                      <th onClick={() => handleSort("kdr")}>KDR {sortIndicator("kdr")}</th>
                      {visibleCols.suicides && (
                        <th onClick={() => handleSort("suicides")}>Suicides {sortIndicator("suicides")}</th>
                      )}
                      {visibleCols.damage && (
                        <th onClick={() => handleSort("damage")}>Damage {sortIndicator("damage")}</th>
                      )}
                      {visibleCols.playtime && <th>Playtime</th>}
                      {visibleCols.sniperKills && (
                        <th onClick={() => handleSort("sniperKills")}>
                          Sniper Hunter Kills {sortIndicator("sniperKills")}
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedRows.map((row, idx) => {
                      const globalIdx = page * pageSize + idx + 1;
                      const faction = factionMap.get(row.factionId);
                      return (
                        <tr key={row.userId}>
                          <td className={styles.rankCell}>{globalIdx}.</td>
                          <td className={styles.nameCell}>
                            {faction && (
                              <span className={styles.factionTag}>[{faction.tag}]</span>
                            )}
                            {row.name.replace(/_/g, " ")}
                            {row.sniperKills > 0 && (
                              <img src="/samp/icons/sniper.png" alt="sniper" className={styles.sniperIcon} title="Sniper Hunter" />
                            )}
                          </td>
                          <td>{row.kills}</td>
                          <td>{row.deaths}</td>
                          <td>{getKdr(row.kills, row.deaths)}</td>
                          {visibleCols.suicides && <td>{row.suicides}</td>}
                          {visibleCols.damage && <td>{row.damage.toLocaleString()}</td>}
                          {visibleCols.playtime && <td>0 hours</td>}
                          {visibleCols.sniperKills && <td>{row.sniperKills}</td>}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className={styles.pagination}>
                <span className={styles.pageSizeLabel}>
                  Items per page:
                  <select
                    className={styles.pageSizeSelect}
                    value={pageSize}
                    onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
                  >
                    {[25, 50, 100].map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </span>
                <span className={styles.pageInfo}>
                  {page * pageSize + 1}–{Math.min((page + 1) * pageSize, sortedRows.length)} of {sortedRows.length}
                </span>
                <button className={styles.pageBtn} disabled={page === 0} onClick={() => setPage(0)}>
                  {"<|"}
                </button>
                <button className={styles.pageBtn} disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
                  {"<"}
                </button>
                <button className={styles.pageBtn} disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}>
                  {">"}
                </button>
                <button className={styles.pageBtn} disabled={page >= totalPages - 1} onClick={() => setPage(totalPages - 1)}>
                  {">|"}
                </button>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default WarsPage;
